import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Calendar, Activity, Download } from "lucide-react";
import Navbar from "../components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
}

export default function PatientMedicalRecords() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/patient/medical-records")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const openRxPdf = async (rxId) => {
    try {
      const res = await api.get(`/prescriptions/${rxId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="patient-records-page"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-5xl w-full mx-auto space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">My medical records</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Past consults, prescriptions, lab reports, vitals.
          </p>
        </div>

        <Tabs defaultValue="prescriptions">
          <TabsList className="bg-secondary p-1 rounded-full h-12 flex-wrap">
            <TabsTrigger
              value="prescriptions"
              data-testid="records-tab-rx"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Prescriptions
            </TabsTrigger>
            <TabsTrigger
              value="past"
              data-testid="records-tab-past"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Past consults
            </TabsTrigger>
            <TabsTrigger
              value="reports"
              data-testid="records-tab-reports"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Lab reports
            </TabsTrigger>
            <TabsTrigger
              value="vitals"
              data-testid="records-tab-vitals"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Vitals
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prescriptions" className="mt-6 space-y-3">
            {loading ? (
              <div className="h-24 bg-muted rounded-2xl animate-pulse" />
            ) : (data?.prescriptions || []).length === 0 ? (
              <EmptyCard icon={FileText} title="No prescriptions yet" />
            ) : (
              data.prescriptions.map((rx) => (
                <div
                  key={rx.id}
                  data-testid={`records-rx-${rx.id}`}
                  className="bg-white border border-border rounded-2xl p-5 flex items-center gap-4"
                >
                  <span className="h-11 w-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{rx.doctor?.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {rx.doctor?.category} · {fmt(rx.created_at)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {(rx.medicines || []).length} medicine(s){" "}
                      {rx.diagnosis ? `· ${rx.diagnosis}` : ""}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    data-testid={`records-rx-${rx.id}-download`}
                    onClick={() => openRxPdf(rx.id)}
                    className="rounded-full"
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Open
                  </Button>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6 space-y-3">
            {loading ? (
              <div className="h-24 bg-muted rounded-2xl animate-pulse" />
            ) : (data?.past_appointments || []).length === 0 ? (
              <EmptyCard icon={Calendar} title="No past consults yet" />
            ) : (
              data.past_appointments.map((a) => (
                <Link
                  to={`/patient/appointments/${a.id}`}
                  key={a.id}
                  data-testid={`records-past-${a.id}`}
                  className="block bg-white border border-border rounded-2xl p-5 hover:shadow-md transition-shadow"
                >
                  <div className="font-semibold">{a.doctor?.name}</div>
                  <div className="text-xs text-muted-foreground">{a.doctor?.category}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {fmt(a.scheduled_at)} · {a.status}
                  </div>
                </Link>
              ))
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <EmptyCard
              icon={Activity}
              title="Lab reports coming soon"
              subtitle="Shared lab reports will appear here in Phase 4."
            />
          </TabsContent>

          <TabsContent value="vitals" className="mt-6">
            <VitalsHistoryTable />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function VitalsHistoryTable() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/patient/me/vitals-history")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="h-24 bg-muted rounded-2xl animate-pulse" />;
  if (items.length === 0) {
    return (
      <EmptyCard
        icon={Activity}
        title="No vitals recorded yet"
        subtitle="Vitals are added automatically after diagnostic tests at a clinic."
      />
    );
  }
  return (
    <div data-testid="vitals-history-table" className="bg-white border border-border rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">BP</th>
              <th className="text-left p-3">HR</th>
              <th className="text-left p-3">SpO₂</th>
              <th className="text-left p-3">Temp</th>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => {
              const bp = v.bp_systolic && v.bp_diastolic ? `${v.bp_systolic}/${v.bp_diastolic}` : "—";
              return (
                <tr key={v.id} data-testid={`vital-row-${v.id}`} className="border-t border-border">
                  <td className="p-3 text-xs text-muted-foreground">{fmt(v.recorded_at)}</td>
                  <td className="p-3 tabular-nums">{bp}</td>
                  <td className="p-3 tabular-nums">{v.hr ?? "—"}</td>
                  <td className="p-3 tabular-nums">{v.spo2 != null ? `${v.spo2}%` : "—"}</td>
                  <td className="p-3 tabular-nums">{v.temp_c != null ? `${v.temp_c}°C` : "—"}</td>
                  <td className="p-3 text-xs">
                    {v.appointment_id ? (
                      <Link to={`/patient/appointments/${v.appointment_id}`} className="text-primary hover:underline font-mono">
                        {v.appointment_id}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="p-3">
                    <span
                      className={
                        "text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " +
                        (v.abnormal_flag ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700")
                      }
                    >
                      {v.abnormal_flag ? "Abnormal" : "Normal"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyCard({ icon: Icon, title, subtitle }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-10 text-center">
      <Icon className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
      <div className="font-semibold text-lg mb-1">{title}</div>
      {subtitle && <div className="text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
